import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Wand2, Rocket, CheckCircle2, Clock, Coins, Sparkles, ArrowRight,
    FileText, Layers, Package, Gauge, FileDown, ArrowUpRight,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const PROJECT_TYPES = [
    "Gold Processing Plant (CIP/CIL)",
    "Copper Concentrator",
    "PGM Smelter",
    "Iron Ore Beneficiation",
    "Coal Beneficiation",
    "Cobalt Refinery",
    "Ammonia Facility",
    "Green Hydrogen Terminal",
    "Water / Desalination",
];

const CLIENTS = [
    "Sibanye-Stillwater", "Anglo American", "Kumba Iron Ore", "Harmony Gold",
    "First Quantum", "Khoemacau Copper", "OCP Africa", "Hive Hydrogen",
    "Vale Moçambique", "ERG Africa",
];

const REGIONS = [
    "South Africa", "Mozambique", "Botswana", "DRC", "Zambia", "Namibia",
    "Ghana", "Australia", "Chile", "Peru",
];

export default function BaselineMyProject() {
    const [config, setConfig] = useState({
        type: PROJECT_TYPES[0],
        scale: "4 Mtpa",
        client: CLIENTS[0],
        region: REGIONS[0],
    });
    const [baseline, setBaseline] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

    const generate = async () => {
        setBusy(true);
        setBaseline(null);
        try {
            const { data } = await api.post("/edi/baseline", config);
            setBaseline(data);
            toast.success("Baseline generated");
        } catch {
            toast.error("Baseline generation failed");
        } finally {
            setBusy(false);
        }
    };

    const downloadBidPack = async () => {
        if (!baseline) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem("sgsb.token");
            const title = `Executive Bid Pack — ${config.type} · ${config.client}`;
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title,
                    content: baseline.bid_pack_markdown,
                    fmt: "pdf",
                    confidentiality: "Executive",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "SGSBateman_Executive_Bid_Pack.pdf";
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success("Executive Bid Pack downloaded");
        } catch {
            toast.error("Bid pack export failed");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid={EDI.baselineRoot}>
            {/* Step 1 — Configure */}
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.22), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.16), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <Wand2 className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">One-click baseline</span>
                    </div>
                    <h3 className="mt-1 font-display font-black text-white text-2xl tracking-tight">
                        Baseline My New Project™
                    </h3>
                    <p className="mt-1 text-slate-400 text-sm font-body max-w-2xl">
                        Configure the new opportunity — SGS Bateman EPCM360™ analyses the entire knowledge base,
                        matches similar historical delivery, and pre-populates the engineering, procurement and
                        commercial baseline in seconds.
                    </p>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">Project type</label>
                            <select
                                data-testid={EDI.baselineType}
                                value={config.type}
                                onChange={(e) => set("type", e.target.value)}
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            >
                                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">Scale / throughput</label>
                            <input
                                data-testid={EDI.baselineScale}
                                value={config.scale}
                                onChange={(e) => set("scale", e.target.value)}
                                placeholder="e.g. 4 Mtpa"
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            />
                        </div>
                        <div>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">Client</label>
                            <select
                                data-testid={EDI.baselineClient}
                                value={config.client}
                                onChange={(e) => set("client", e.target.value)}
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            >
                                {CLIENTS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">Region</label>
                            <select
                                data-testid={EDI.baselineRegion}
                                value={config.region}
                                onChange={(e) => set("region", e.target.value)}
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            >
                                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                    </div>

                    <button
                        data-testid={EDI.baselineGenerate}
                        disabled={busy}
                        onClick={generate}
                        className="mt-5 inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-5 py-3 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <Sparkles className="h-4 w-4" strokeWidth={2} />
                        {busy ? "Baselining…" : "Baseline My New Project™"}
                    </button>
                </div>
            </div>

            {/* Step 2 — Results */}
            {baseline && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            ["Hours Saved",  `${baseline.hours_saved.toLocaleString()}`, "Engineering", Clock],
                            ["Cost Saved",   baseline.cost_saved,                        "Portfolio impact", Coins],
                            ["Reuse Rate",   `${baseline.reuse_pct}%`,                   "This opportunity", Layers],
                            ["Confidence",   `${baseline.confidence}%`,                  "Baseline strength", Gauge],
                        ].map(([k, v, s, Icon]) => (
                            <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                                <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <div className="mt-2 kicker text-slate-400 text-[10px]">{k}</div>
                                <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                                <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                            </div>
                        ))}
                    </div>

                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center justify-between">
                            <h3 className="font-display font-black text-white tracking-tight">Similar historical projects</h3>
                            <span className="kicker text-slate-500 text-[10px]">{baseline.similar_projects.length} matches</span>
                        </div>
                        <table className="w-full text-sm font-body">
                            <thead>
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Project</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Client</th>
                                    <th className="text-right px-4 py-3">Similarity</th>
                                    <th className="text-right px-4 py-3">Est. hrs saved</th>
                                </tr>
                            </thead>
                            <tbody>
                                {baseline.similar_projects.map((p, i) => (
                                    <tr
                                        key={i}
                                        className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-200"
                                        style={{
                                            opacity: 0,
                                            animation: "sgsb-fade-in 400ms ease forwards",
                                            animationDelay: `${i * 80}ms`,
                                        }}
                                    >
                                        <td className="px-4 py-2.5 text-slate-100">{p.name}</td>
                                        <td className="px-4 py-2.5 text-slate-300">{p.type}</td>
                                        <td className="px-4 py-2.5 text-slate-400">{p.client}</td>
                                        <td className="px-4 py-2.5 text-right text-[var(--sgsb-gold)] font-display font-bold">{p.similarity}%</td>
                                        <td className="px-4 py-2.5 text-right text-slate-200">{p.hours_saved.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        <div className="glass rounded-xl overflow-hidden">
                            <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                <Package className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <h3 className="font-display font-black text-white tracking-tight">Recommended reusable assets</h3>
                            </div>
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-4 py-3">Category</th>
                                        <th className="text-right px-4 py-3">Assets</th>
                                        <th className="text-right px-4 py-3">Confidence</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {baseline.reusable_assets.map((r, i) => (
                                        <tr key={i} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-200"
                                            style={{
                                                opacity: 0,
                                                animation: "sgsb-fade-in 400ms ease forwards",
                                                animationDelay: `${i * 90}ms`,
                                            }}
                                        >
                                            <td className="px-4 py-2.5 text-slate-100">{r.category}</td>
                                            <td className="px-4 py-2.5 text-right text-slate-200 font-display font-bold">{r.count}</td>
                                            <td className="px-4 py-2.5 text-right">
                                                <span className="text-[11px] rounded-full px-2 py-0.5 border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)]">
                                                    {r.confidence}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="glass rounded-xl p-5">
                            <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                                <FileText className="h-4 w-4" strokeWidth={1.5} />
                                <span className="kicker text-[10px]">Baseline generated</span>
                            </div>
                            <h3 className="mt-1 font-display font-black text-white text-lg tracking-tight">
                                Auto-populated project structure
                            </h3>
                            <ul className="mt-3 space-y-2 text-slate-200 font-body text-sm">
                                {baseline.artifacts.map((a, i) => (
                                    <li key={i} className="flex gap-2 items-start"
                                        style={{
                                            opacity: 0,
                                            animation: "sgsb-fade-in 400ms ease forwards",
                                            animationDelay: `${i * 90}ms`,
                                        }}
                                    >
                                        <CheckCircle2 className="h-4 w-4 text-[var(--sgsb-gold)] shrink-0 mt-0.5" strokeWidth={2} />
                                        <span>{a}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-5 flex flex-wrap gap-3">
                                <button
                                    onClick={downloadBidPack}
                                    disabled={downloading}
                                    data-testid={EDI.baselineExport}
                                    className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                                >
                                    <FileDown className="h-4 w-4" strokeWidth={2} />
                                    {downloading ? "Preparing…" : "Generate Executive Bid Pack (PDF)"}
                                </button>
                                <Link
                                    to="/edi/business-value"
                                    className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                                >
                                    <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                                    See Business Value
                                </Link>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                @keyframes sgsb-fade-in {
                    from { opacity: 0; transform: translateY(4px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
